import { createResource, type RouteResource } from 'react-resource-router';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type {
	AiNotificationPolicyResponse,
	AlertPolicyResponse,
	GlobalAlertPolicyResponse,
	NotificationPolicyResponse,
	PolicyMaintenanceRights,
} from '../../common/types';
import {
	getGlobalPolicies,
	getNotificationPolicies,
	getPolicyMaintenanceRights,
	getTeamPolicies,
} from '../endpoints';
import { globalGroupNotificationPolicyResource } from '../notification/global';
import { groupNotificationPolicyResource } from '../notification/group';

export const teamPolicyResource: RouteResource<AlertPolicyResponse | null> = createResource({
	type: 'teamPolicyResource',
	getKey: (routerContext) => {
		const teamId = routerContext.match.params?.teamId;

		return `team-policy/${teamId ?? ''}`;
	},
	getData: async (routerContext) => {
		const teamId = routerContext.match.params?.teamId;

		// if there is no teamId, this means that we are on the global policy page.
		// so we can return null directly, instead of sending fetch request.
		if (!teamId) {
			return null;
		}

		return getTeamPolicies(teamId);
	},
	isBrowserOnly: true,
});

export const notificationPolicyResource: RouteResource<NotificationPolicyResponse | undefined> =
	createResource({
		type: 'notificationPolicyResource',
		getKey: (routerContext) => {
			const teamId = routerContext.match.params?.teamId;

			return `notification-policy/${teamId}`;
		},
		getData: async (routerContext) => {
			const teamId = routerContext.match.params?.teamId;

			if (!teamId) {
				return;
			}

			return getNotificationPolicies(teamId);
		},
		isBrowserOnly: true,
	});

export const globalPolicyResource: RouteResource<GlobalAlertPolicyResponse> = createResource({
	type: 'globalPolicyResource',
	getKey: (routerContext) => {
		const teamId = routerContext.match.params?.teamId;

		if (teamId) {
			return `global-policy/${teamId}`;
		}

		return 'global-policy';
	},
	getData: async (routerContext) => {
		const teamId = routerContext.match.params?.teamId;

		return getGlobalPolicies(teamId);
	},
	isBrowserOnly: true,
});

export const policyMaintenanceRightsResource: RouteResource<PolicyMaintenanceRights> =
	createResource({
		type: 'policyMaintenanceRightsResource',
		getKey: () => 'policy-maintenance-rights-resource',
		getData: async () => {
			return getPolicyMaintenanceRights();
		},
		isBrowserOnly: true,
	});

export { globalGroupNotificationPolicyResource, groupNotificationPolicyResource };

export const resources = (): (
	| RouteResource<PolicyMaintenanceRights>
	| RouteResource<AlertPolicyResponse | null>
	| RouteResource<NotificationPolicyResponse | undefined>
	| RouteResource<GlobalAlertPolicyResponse>
	| RouteResource<AiNotificationPolicyResponse | undefined>
)[] => [
	teamPolicyResource,
	globalPolicyResource,
	policyMaintenanceRightsResource,
	notificationPolicyResource,
	...(fg('eoc_ai_notification_policies')
		? [groupNotificationPolicyResource, globalGroupNotificationPolicyResource]
		: []),
];
